import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	Output,
	ViewChild,
} from '@angular/core';
import { fuzzyFilter, FuzzySearchable } from 'src/app/utils/fuzzy-search.util';

export interface SearchableSelectOption<T = unknown> extends FuzzySearchable {
	value: T;
	disabled?: boolean;
}

@Component({
	selector: 'app-searchable-select',
	templateUrl: './searchable-select.component.html',
	styleUrls: ['./searchable-select.component.scss'],
})
export class SearchableSelectComponent<T = unknown> {
	@ViewChild('input') inputRef!: ElementRef<HTMLInputElement>;

	@Input() options: SearchableSelectOption<T>[] = [];
	@Input() placeholder = 'Select';
	@Input() disabled = false;
	@Input() testId?: string;

	@Output() selected = new EventEmitter<T>();

	query = '';
	isOpen = false;
	activeIndex = -1;
	filtered: SearchableSelectOption<T>[] = [];

	readonly listId = `searchable-select-${Math.random().toString(36).slice(2)}`;

	constructor(private host: ElementRef<HTMLElement>) {}

	/** Called by the page after a scan is handled, so scanner keystrokes never linger. */
	clear(): void {
		this.query = '';
		this.close();
		this.inputRef?.nativeElement.blur();
	}

	open(): void {
		if (this.disabled) return;
		this.filtered = fuzzyFilter(this.options, this.query);
		this.isOpen = true;
		this.activeIndex = this.filtered.length ? 0 : -1;
	}

	close(): void {
		this.isOpen = false;
		this.activeIndex = -1;
	}

	toggle(): void {
		if (this.isOpen) {
			this.close();
		} else {
			this.inputRef.nativeElement.focus();
			this.open();
		}
	}

	onInput(value: string): void {
		this.query = value;
		this.open();
	}

	onKeydown(event: KeyboardEvent): void {
		if (!this.isOpen && (event.key === 'ArrowDown' || event.key === 'Enter')) {
			this.open();
			event.preventDefault();
			return;
		}
		switch (event.key) {
			case 'ArrowDown':
				this.activeIndex = Math.min(this.activeIndex + 1, this.filtered.length - 1);
				event.preventDefault();
				break;
			case 'ArrowUp':
				this.activeIndex = Math.max(this.activeIndex - 1, 0);
				event.preventDefault();
				break;
			case 'Enter':
				if (this.activeIndex >= 0) this.choose(this.filtered[this.activeIndex]);
				event.preventDefault();
				break;
			case 'Escape':
				this.close();
				break;
		}
	}

	choose(option: SearchableSelectOption<T>): void {
		if (option.disabled) return;
		this.selected.emit(option.value);
		this.clear();
	}

	@HostListener('document:pointerdown', ['$event'])
	onDocumentPointerDown(event: PointerEvent): void {
		if (!this.host.nativeElement.contains(event.target as Node)) this.close();
	}

	trackByLabel(_: number, option: SearchableSelectOption<T>): string {
		return option.label;
	}
}
